import { findLesson } from "./_helpers";
import { LessonShell } from "@/components/LessonShell";
import { AnimatedExplainer } from "@/components/AnimatedExplainer";
import {
  SAPSection,
  OomPahPlayer,
  SAPChordRow,
  SAPCallout,
  SAPQuote,
  SAPPracticeLadder,
} from "@/components/sap/SAPShell";
import { RomanNumeralAnalysis } from "@/components/song/RomanNumeralAnalysis";
import { YouTubeEmbed } from "@/components/song/YouTubeEmbed";
import { NoteImage, NoteImagePair } from "@/components/mt/NoteImage";

export default function SingAndPlay10() {
  const { lesson, course, accent } = findLesson("sing-and-play", "10");
  return (
    <LessonShell
      course={course}
      courseSlug="sing-and-play"
      lessonOrder={lesson.order}
      title={lesson.title}
      description={lesson.desc}
      videoUrl={lesson.video}
      thumbUrl={lesson.thumb}
      accent={accent}
    >

      <AnimatedExplainer
        src="/animations/sing-and-play/SP10-Oblahdi-Animated.mp4"
        caption={`Ska/pop bounce — quarter + two eighths · long-short-short`}
        hint="≈ 22 sec · the bounce groove"
      />
      {/* ── 1. Big idea ─────────────────────────────────────────────────── */}
      <SAPSection eyebrow="Lesson 10 · The Beatles" title="Ob-La-Di, Ob-La-Da — The Quarter + Two Eighths Engine">
        <p>
          The Beatles recorded "Ob-La-Di, Ob-La-Da" in 1968 for the White Album. It
          bounces, it skips, it insists on its own joy. That irresistible forward motion
          comes from a single rhythmic pattern: <strong>quarter note + two eighth
          notes</strong> in the left hand — a pulse that works like a three-cylinder
          engine, never settling into complete regularity, always leaning forward.
        </p>
        <p>
          This lesson is not really about The Beatles. It's about building a{" "}
          <strong>universal accompaniment template</strong> for upbeat pop songs in
          4/4. The pattern you learn here — LH oom-pah-pah, RH off-beat hits — applies
          directly to dozens of songs: Twist and Shout, Brown Eyed Girl, Sweet Caroline,
          and most of the Beatles' early catalogue.
        </p>
        <SAPCallout label="What we're actually learning" variant="insight">
          By the end of this lesson you will have: (1) a left-hand pattern that generates
          rhythmic momentum automatically, (2) a right-hand placement that adds harmonic
          colour without cluttering the beat, and (3) a transposition habit — the same
          template in C, G, F, and D. One song; four keys; lifetime toolkit.
        </SAPCallout>
      </SAPSection>

      {/* ── 2. Reference video ──────────────────────────────────────────── */}
      <YouTubeEmbed
        videoId="_J9NpHKrKMw"
        title="Reference · The Beatles — Ob-La-Di, Ob-La-Da"
        caption="The Beatles — Ob-La-Di, Ob-La-Da (White Album, 1968). Listen to the piano in the left channel — that bouncing bass line is precisely the LH pattern we're building."
      />

      {/* ── 3. The LH pattern ───────────────────────────────────────────── */}
      <SAPSection eyebrow="Technique · Left Hand" title="Quarter + Two Eighths: The Engine">
        <p>
          The left hand plays this pattern per beat group: one quarter note (the root),
          then two eighth notes (alternating root and fifth). In 4/4, a full bar looks
          like: <strong>Root — Root/Fifth — Root — Root/Fifth —</strong> with the last two
          "Root/Fifth" pairs each taking half a beat.
        </p>
        <p>
          In D major, starting on bar 1:
        </p>
        <ul className="ml-5 list-disc space-y-1">
          <li><strong>Beat 1:</strong> D (quarter note, full beat)</li>
          <li><strong>Beat 2 &amp; (split):</strong> D (eighth) then the A above it (eighth)</li>
          <li><strong>Beat 3:</strong> D (quarter note, full beat)</li>
          <li><strong>Beat 4 &amp; (split):</strong> D (eighth) then the A above it (eighth)</li>
        </ul>
        <p>
          The result is: <em>"Oom — pah-pah — oom — pah-pah."</em> Three notes per
          two-beat group. It divides the bar into two equal "oom-pah-pah" units, and that
          triple subdivision is what gives the pattern its skip.
        </p>

        <SAPCallout label="The alternating root/fifth" variant="tip">
          The two eighth notes don't have to be root–fifth. You can use root–root for a
          simpler feel, or root–third for a richer colour. But root–fifth is the most
          versatile: it outlines the chord clearly without overloading the texture.
          On D: D–A. On G: G–D. On A: A–E. On Bm: B–F♯ (each time, the fifth above the root).
        </SAPCallout>
      </SAPSection>

      {/* ── 4. The chords ───────────────────────────────────────────────── */}
      <SAPSection eyebrow="The Harmony" title="D Major: I–IV–V–vi with a Surprise Visitor">
        <p>
          The verse of Ob-La-Di uses a straightforward D major progression:
          D–G–A–Bm–G–D (I–IV–V–vi–IV–I). The chorus adds a brief detour through{" "}
          <strong>F♯ major</strong> — a chord that doesn't belong in the key of D major
          but appears anyway as a momentary colour. Think of it as a
          surprise visitor: it knocks once and leaves quickly, but its presence makes
          the harmonic landscape more interesting.
        </p>

        <RomanNumeralAnalysis
          label="Verse — I IV V vi IV I"
          caption="The verse sweeps through the full range of the key: from tonic (D) up to subdominant (G), dominant (A), relative minor (Bm), back to subdominant, and home. A complete harmonic story in six chords."
          chords={[
            { chord: "D",  rn: "I",  function: "Tonic",        pitches: ["D3","F#3","A3","D4"] },
            { chord: "G",  rn: "IV", function: "Pre-dominant", pitches: ["G3","B3","D4","G4"]  },
            { chord: "A",  rn: "V",  function: "Dominant",     pitches: ["A3","C#4","E4","A4"] },
            { chord: "Bm", rn: "vi", function: "Submediant",      pitches: ["B3","D4","F#4","B4"] },
            { bar: true },
            { chord: "G",  rn: "IV", function: "Pre-dominant", pitches: ["G3","B3","D4","G4"]  },
            { chord: "D",  rn: "I",  function: "Tonic",        pitches: ["D3","F#3","A3","D4"] },
          ]}
        />

        <RomanNumeralAnalysis
          label="Bridge — the surprise visitor (F♯)"
          caption="F♯ major is built on the third degree of D major, but it uses a major third (A♯/B♭) rather than the minor third the key would normally produce. It's a borrowed moment — unexpected, bright, and over before you know it."
          chords={[
            { chord: "D",  rn: "I",   function: "Tonic",       pitches: ["D3","F#3","A3","D4"] },
            { chord: "F♯", rn: "III", function: "Borrowed",    pitches: ["F#3","A#3","C#4","F#4"] },
            { chord: "G",  rn: "IV",  function: "Pre-dominant",pitches: ["G3","B3","D4","G4"]  },
            { chord: "A",  rn: "V",   function: "Dominant",    pitches: ["A3","C#4","E4","A4"] },
          ]}
        />

        <SAPChordRow
          label="All chords — click to hear"
          chords={[
            { label: "D",  rn: "I",   pitches: ["D3","F#3","A3","D4"],    fn: "tonic"       },
            { label: "G",  rn: "IV",  pitches: ["G3","B3","D4","G4"],     fn: "subdominant" },
            { label: "A",  rn: "V",   pitches: ["A3","C#4","E4","A4"],    fn: "dominant"    },
            { label: "Bm", rn: "vi",  pitches: ["B3","D4","F#4","B4"],    fn: "submediant"     },
            { label: "F♯", rn: "III", pitches: ["F#3","A#3","C#4","F#4"], fn: "borrowed"    },
          ]}
          caption="Click F♯ — hear how it doesn't quite belong in D major but sounds perfectly at home in the song. That's the magic of the surprise visitor."
        />
      </SAPSection>

      {/* ── 5. RH off-beat hits ─────────────────────────────────────────── */}
      <SAPSection eyebrow="Technique · Right Hand" title="Off-Beat Hits: The Ob-La-Di Bounce">
        <p>
          The right hand in this song plays <strong>only on the off-beats</strong> — the
          "&" of every beat. While the LH is playing its quarter-note roots on beats 1,
          2, 3, and 4, the RH is silent on those numbered beats and hits the chord only
          between them.
        </p>
        <p>
          The effect is that the two hands <em>never land at the same time</em> — they
          pass each other like two dancers on alternating footsteps. LH on the numbers,
          RH on the &'s. Together they fill every eighth-note slot in the bar, creating
          a continuous pulse with internal momentum.
        </p>

        <SAPCallout label="Where the bounce comes from" variant="insight">
          The bounce is a product of <em>weight transfer</em>. The LH lands with authority
          (the oom); the RH rebounds lightly (the pah). Practice the LH with a slightly
          heavier touch than the RH — let the bass carry the groove, and let the RH sit
          above it like a light percussion layer. That balance is what makes it bounce
          rather than clank.
        </SAPCallout>

        <SAPQuote>
          It's not about the piano. The piano is to support your voice or whoever you're
          accompanying. Your job on the keys is to support them or yourself. That's the
          point. And you have to keep the piano playing in autopilot as best as
          possible.
        </SAPQuote>
      </SAPSection>

      {/* ── 6. Connecting notes — walking between chords ────────────────── */}
      <SAPSection eyebrow="Advanced Technique" title="Walking Between Chords — No More Gaps">
        <p>
          One of the most powerful techniques in Jason's Ob-La-Di teaching: instead of
          jumping from D directly to G, <strong>walk diatonically</strong> between them.
          D → E → F♯ → G. Four scale steps, four eighth notes, a smooth ascending line
          that fills the harmonic gap with motion.
        </p>
        <p>
          The same works descending: going from A back to D, walk A → G → F♯ → E → D
          (five steps, spread across a bar). These connecting notes aren't chords — they're
          scalar movement that makes the chord changes feel inevitable rather than abrupt.
        </p>



        <SAPCallout label="Walking lines make you sound advanced" variant="tip">
          A student who jumps from chord to chord sounds like a student. A student who
          walks between chords sounds like a musician. The walking line costs you almost
          nothing technically — it's just the scale you already know — but it transforms
          the aesthetic quality of the arrangement immediately.
        </SAPCallout>
      </SAPSection>

      {/* ── 7. OomPah interactive player ────────────────────────────────── */}
      <SAPSection eyebrow="Interactive · Play Along" title="The Ob-La-Di Loop in D Major">
        <p>
          Press play at 105 bpm to feel the Ob-La-Di bounce. Watch the LH lane (amber)
          hitting every beat, and the RH lane (purple) hitting every off-beat. Together
          they form the continuous eighth-note pulse.
        </p>
        <OomPahPlayer
          progression={[
            { label: "D",  bars: 2, lhRoot: "D3",  lhFifth: "A3",  rh: ["D4","F#4","A4"] },
            { label: "G",  bars: 2, lhRoot: "G3",  lhFifth: "D4",  rh: ["G4","B4","D5"]  },
            { label: "A",  bars: 2, lhRoot: "A3",  lhFifth: "E4",  rh: ["A4","C#5","E5"] },
            { label: "Bm", bars: 2, lhRoot: "B3",  lhFifth: "F#4", rh: ["B4","D5","F#5"] },
          ]}
          bpm={105}
          label="OB-LA-DI — D major bounce loop"
        />
      </SAPSection>

      {/* ── 8. Transposition exercise ────────────────────────────────────── */}
      <SAPSection eyebrow="Transposition" title="Same Pattern, Four Keys">
        <p>
          The real payoff of this lesson is portability. Play the I–IV–V–vi loop with the
          quarter + two eighths pattern in C major, G major, and F major. The chord names
          change; everything else stays the same.
        </p>

        <SAPChordRow
          label="In C major — I IV V vi"
          chords={[
            { label: "C",  rn: "I",  pitches: ["C3","E3","G3","C4"],  fn: "tonic"       },
            { label: "F",  rn: "IV", pitches: ["F3","A3","C4","F4"],  fn: "subdominant" },
            { label: "G",  rn: "V",  pitches: ["G3","B3","D4","G4"],  fn: "dominant"    },
            { label: "Am", rn: "vi", pitches: ["A3","C4","E4","A4"],  fn: "submediant"     },
          ]}
          caption="C major: the all-white-key training key. Practice the template here first if D major feels awkward."
        />

        <SAPChordRow
          label="In G major — I IV V vi"
          chords={[
            { label: "G",  rn: "I",  pitches: ["G3","B3","D4","G4"],  fn: "tonic"       },
            { label: "C",  rn: "IV", pitches: ["C3","E3","G3","C4"],  fn: "subdominant" },
            { label: "D",  rn: "V",  pitches: ["D3","F#3","A3","D4"], fn: "dominant"    },
            { label: "Em", rn: "vi", pitches: ["E3","G3","B3","E4"],  fn: "submediant"     },
          ]}
          caption="G major: one sharp. This key sits very comfortably in the hands and is the most common key for folk-pop songs."
        />

        <SAPChordRow
          label="In F major — I IV V vi"
          chords={[
            { label: "F",  rn: "I",  pitches: ["F3","A3","C4","F4"],  fn: "tonic"       },
            { label: "Bb", rn: "IV", pitches: ["Bb2","D3","F3","Bb3"], fn: "subdominant" },
            { label: "C",  rn: "V",  pitches: ["C3","E3","G3","C4"],  fn: "dominant"    },
            { label: "Dm", rn: "vi", pitches: ["D3","F3","A3","D4"],  fn: "submediant"  },
          ]}
          caption="F major: one flat, and the first key here whose IV chord (B♭) needs a black note. Take the B♭ with your thumb tucked slightly forward and the rest of the shape stays identical."
        />

        <SAPCallout label="The transposition habit" variant="tip">
          Every time you learn a new song, immediately try it in one other key. It takes
          five minutes and doubles the value of the practice session. Within a month,
          you'll be transposing fluently without thinking about it — because you've
          trained the pattern, not the notes.
        </SAPCallout>
      </SAPSection>

      {/* ── 9. Practice ladder ──────────────────────────────────────────── */}
      <SAPSection eyebrow="Your Practice Plan" title="35-Minute Ob-La-Di Workout">
        <SAPPracticeLadder
          intro="This lesson is a template drill. Every minute you spend here pays dividends on every bouncy pop song you ever play."
          rungs={[
            {
              title: "LH oom-pah-pah on D alone",
              minutes: 5,
              detail: "D (quarter), then D–A (two eighths). Repeat for 4 bars. Get the \"oom-pah-pah\" feel automatic before adding chord changes.",
              accent: "amber",
            },
            {
              title: "LH through D–G–A–Bm",
              minutes: 5,
              detail: "Two bars per chord, same pattern. Stay at 80 bpm. Focus on the chord transitions — the root changes; the pattern doesn't.",
              accent: "purple",
            },
            {
              title: "RH off-beats — one chord",
              minutes: 5,
              detail: "Hold D chord in RH, hit on the &-of-every-beat only. Numbers = LH only; &-of-numbers = RH only. Build the interlock.",
              accent: "cyan",
            },
            {
              title: "Hands together — D chord",
              minutes: 5,
              detail: "Both hands on D. LH oom-pah-pah; RH off-beat chords. The hands pass each other like a clock. Feel the bounce before adding chord changes.",
              accent: "emerald",
            },
            {
              title: "Full verse with chord changes",
              minutes: 8,
              detail: "D–G–A–Bm–G–D with hands together. Keep it at 90 bpm. Add the F♯ bridge chord when the verse is smooth.",
              accent: "rose",
            },
            {
              title: "Add walking lines",
              minutes: 5,
              detail: "On D→G, walk D–E–F♯–G in the LH. On A→D, walk A–G–F♯–E–D. One connecting phrase per chord change.",
              accent: "amber",
            },
            {
              title: "Sing + play",
              minutes: 2,
              detail: "\"Desmond has a barrel in the marketplace…\" Bring the voice in on top. Triple independence: LH pattern + RH hits + voice. Grin.",
              accent: "purple",
            },
          ]}
        />
        <SAPQuote>
          The point of teaching you this song, it's not necessarily to teach you how to
          sing the song, but how to develop the pattern for songs like this. And there
          are lots.
        </SAPQuote>
      </SAPSection>


      {/* ── Jason's notes ────────────────────────────────────────────── */}
      <SAPSection eyebrow="Jason's chord chart" title="Handwritten teaching notes">
        <p>
          This is Jason's original handwritten chord chart for this song — the same one he
          uses in the video lesson. Click to zoom in and follow along.
        </p>
        <NoteImage
          src="/notes/sing-and-play/10-ob-la-di.png"
          caption="Jason's chord chart — Ob-La-Di Ob-La-Da in B♭ major"
        />
      </SAPSection>
    </LessonShell>
  );
}
